import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  CircularProgress,
  InputAdornment,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  HelpOutline as HelpOutlineIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { fetchTemplates, deleteTemplate } from '../../redux/slices/templateSlice';
import { fetchTemplateCategories } from '../../redux/slices/templateCategorySlice';
import PermissionButton from '../../components/common/PermissionButton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';

/**
 * TemplateListPage - Admin page for listing and managing templates
 * 
 * Features:
 * - Search, category, and scope filters
 * - Pagination
 * - Template cards with actions
 * - Permission-based create button
 * - Delete confirmation dialog
 * 
 * Phase 6A: M3 - Frontend Base Components
 */
const TemplateListPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { templates, loading, pagination, error } = useSelector(
    (state) => state.templates
  );

  const { user } = useSelector((state) => state.auth);
  const categories = useSelector((state) => state.templateCategories.list);

  // Her mount'ta kategorileri fetch et (güncel liste için)
  useEffect(() => {
    dispatch(fetchTemplateCategories());
  }, [dispatch]);

  // Permission-based guard
  const hasPermission = useHasPermission('templates.view');
  
  // Permission checks for button visibility
  // Use useHasPermission hook for consistent permission checking (case-insensitive)
  const hasSystemAdmin = useHasPermission('systemadminpermission');
  const hasCompanyAdmin = useHasPermission('companyadminpermission');
  
  // Helper function - template düzenlenebilir mi?
  const canEditTemplate = (template) => {
    if (hasSystemAdmin) return true; // System admin tümünü düzenleyebilir
    
    if (hasCompanyAdmin && user?.companyId) {
      // Company owner: kendi şirketinin şablonlarını düzenleyebilir
      const templateCompanyId = typeof template.companyId === 'object' 
        ? template.companyId?._id?.toString() 
        : template.companyId?.toString();
      
      // user.companyId bir object ise _id'yi al, değilse direkt toString()
      const userCompanyIdStr = typeof user.companyId === 'object' 
        ? user.companyId?._id?.toString() 
        : user.companyId?.toString();
      
      if (templateCompanyId === userCompanyIdStr) {
        return true;
      }
    }
    
    // Individual veya Company Member: sadece kendi oluşturduğu şablonları
    const templateCreatedBy = typeof template.createdBy === 'object'
      ? template.createdBy?._id?.toString()
      : template.createdBy?.toString();
    const userIdStr = user?._id?.toString();
    
    return templateCreatedBy === userIdStr;
  };
  
  // Helper function - template silinebilir mi?
  const canDeleteTemplate = (template) => {
    // Silme yetkisi düzenleme ile aynı
    return canEditTemplate(template);
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    templateId: null,
    templateTitle: '',
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (hasPermission) {
      const params = {
        search: debouncedSearch,
        category: categoryFilter,
        isPublic: scopeFilter,
        page: page + 1,
        limit,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      dispatch(fetchTemplates(params));
    }
  }, [dispatch, debouncedSearch, categoryFilter, scopeFilter, page, limit, hasPermission]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle category filter
  const handleCategoryFilter = (e) => {
    setCategoryFilter(e.target.value);
  };

  // Handle scope filter
  const handleScopeFilter = (e) => {
    setScopeFilter(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (template) => {
    setDeleteDialog({
      open: true,
      templateId: template._id,
      templateTitle: template.title,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.templateId) {
      await dispatch(deleteTemplate(deleteDialog.templateId));
      setDeleteDialog({ open: false, templateId: null, templateTitle: '' });

      const params = {
        search: debouncedSearch,
        category: categoryFilter,
        isPublic: scopeFilter,
        page: page + 1,
        limit,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      dispatch(fetchTemplates(params));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, templateId: null, templateTitle: '' });
  };

  const handleEdit = (templateId) => {
    history.push(`/templates/${templateId}/edit`);
  };

  const handlePreview = (templateId) => {
    history.push(`/templates/${templateId}/preview`);
  };

  const handleCreate = () => {
    history.push('/templates/new');
  };

  const handleBack = () => {
    history.goBack();
  };

  // Permission-based guard
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Şablonlar
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Doküman şablonlarını görüntüleyin ve yönetin
          </Typography>
        </Box>
        <PermissionButton
          permission="templates.create"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Yeni Şablon
        </PermissionButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField
            placeholder="Şablon adı veya açıklama ile ara..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ flex: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Category Filter */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Kategori</InputLabel>
            <Select
              value={categoryFilter}
              label="Kategori"
              onChange={handleCategoryFilter}
            >
              <MenuItem value="">Tümü</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat._id} value={cat.name}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Scope Filter */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Kapsam</InputLabel>
            <Select
              value={scopeFilter}
              label="Kapsam"
              onChange={handleScopeFilter}
            >
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="true">Genel Şablonlar</MenuItem>
              <MenuItem value="false">Şirket Şablonları</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading && templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Şablonlar yükleniyor...
          </Typography>
        </Paper>
      ) : !loading && templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {debouncedSearch || categoryFilter || scopeFilter ? 'Şablon bulunamadı' : 'Henüz şablon oluşturmadınız'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {!debouncedSearch && !categoryFilter && !scopeFilter && 'Yeni bir şablon oluşturmak için "Yeni Şablon" butonuna tıklayın'}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Başlık</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Kapsam</TableCell>
                  <TableCell>Soru Sayısı</TableCell>
                  <TableCell>Kredi Ücreti</TableCell>
                  <TableCell>Kullanım</TableCell>
                  <TableCell>Oluşturan</TableCell>
                  <TableCell>Şirket</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Oluşturulma</TableCell>
                  <TableCell>Güncelleme</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {template.title || 'Başlıksız Şablon'}
                      </Typography>
                      {template.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {template.description.length > 50 
                            ? `${template.description.substring(0, 50)}...` 
                            : template.description}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={template.category || 'Kategori yok'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          template.companyId 
                            ? 'Şirket' 
                            : (template.isPublic ? 'Genel' : 'Özel')
                        }
                        color={
                          template.companyId 
                            ? 'secondary' 
                            : (template.isPublic ? 'info' : 'default')
                        }
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HelpOutlineIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {template.questions?.length || 0}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccountBalanceIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight="medium">
                          {template.creditCost !== null && template.creditCost !== undefined 
                            ? `${parseFloat(template.creditCost).toFixed(1)} Kredi`
                            : '1 Kredi'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DescriptionIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          {template.usageCount || 0}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        doküman
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {template.createdBy ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {typeof template.createdBy === 'object' && template.createdBy.firstName
                              ? `${template.createdBy.firstName} ${template.createdBy.lastName || ''}`.trim()
                              : 'Bilinmiyor'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {template.companyId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {typeof template.companyId === 'object' 
                              ? (template.companyId.companyName || template.companyId.name || 'Bilinmiyor')
                              : 'Bilinmiyor'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={template.isActive !== false ? <CheckCircleIcon /> : <CancelIcon />}
                        label={template.isActive !== false ? 'Aktif' : 'Pasif'}
                        color={template.isActive !== false ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {template.createdAt
                          ? format(new Date(template.createdAt), 'dd MMM yyyy', { locale: tr })
                          : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.createdAt
                          ? format(new Date(template.createdAt), 'HH:mm', { locale: tr })
                          : ''}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {template.updatedAt
                          ? format(new Date(template.updatedAt), 'dd MMM yyyy', { locale: tr })
                          : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.updatedAt
                          ? format(new Date(template.updatedAt), 'HH:mm', { locale: tr })
                          : ''}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {canEditTemplate(template) && (
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(template._id);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Önizle">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(template._id);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canDeleteTemplate(template) && (
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(template);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total || templates.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Sayfa başına:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        title="Şablonu Sil"
        message={`"${deleteDialog.templateTitle}" şablonunu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
        severity="error"
      />
    </Container>
  );
};

export default TemplateListPage;

